// packages/studio/web/src/proposals/useChangesetDiff.ts
//
// Fetches the model diff for a changeset in ANY state (draft / proposed /
// accepted / archived) — the server builds "after" from the changeset's
// effective content (branch head, or base + DB draft overlay). Same payload
// shape as the ref-to-ref /diff endpoint, so it feeds DiffBoard directly.
//
// staleTime: Infinity for proposed/accepted/archived (OIDs are frozen → diff
// is deterministic and never changes). draft uses 60s (content can change).

import { useQuery } from "@tanstack/react-query";
import { deserializeDiffPayload, type DiffPayload } from "../diff/useGitDiff";

type ChangesetState = "draft" | "proposed" | "accepted" | "archived";

export function useChangesetDiff(
  slug: string,
  repo: string,
  id: string,
  enabled = true,
  state?: ChangesetState,
) {
  const staleTime = state === "draft" || state == null ? 60 * 1000 : Infinity;
  return useQuery({
    queryKey: ["changeset-diff", slug, repo, id],
    queryFn: async ({ signal }): Promise<DiffPayload> => {
      const url = `/api/ws/${encodeURIComponent(slug)}/r/${encodeURIComponent(repo)}/proposals/${encodeURIComponent(id)}/diff`;
      const r = await fetch(url, { credentials: "include", signal });
      if (!r.ok) throw new Error(`diff failed: ${r.status}`);
      return deserializeDiffPayload(await r.json());
    },
    enabled: enabled && Boolean(id),
    staleTime,
  });
}
